import type { ReactNode } from "react";
import { CAlert } from "@coreui/react-pro";

interface FailureAlertProps {
  message: string;
  /** The failed request's trace id, when the server sent one. */
  reference?: string;
  className?: string;
  /** Actions rendered under the reference — a dismiss control on the anchored bar, say. */
  children?: ReactNode;
}

/**
 * A failure the member can read at their own pace. Full width of whatever contains it, because a
 * 32-character reference has to be legible and copyable, and because nobody should be racing a timer
 * to write one down.
 *
 * Used two ways: anchored to the bottom of the page for a page action, and inside a dialog for an
 * action the dialog owns.
 */
export function FailureAlert({ message, reference, className, children }: FailureAlertProps) {
  return (
    <CAlert color="danger" className={className}>
      <div>{message}</div>
      {reference && (
        <>
          <div className="small mt-2">If you contact us, quote this reference:</div>
          {/* Inherits the alert's own colour: code defaults to --cui-code-color, a pink that lands
              nearly invisible on the danger alert's ground. user-select-all so one click takes the
              whole id — a half-copied reference resolves to nothing. */}
          <code className="user-select-all" style={{ color: "inherit" }}>
            {reference}
          </code>
        </>
      )}
      {children}
    </CAlert>
  );
}
